import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Grid,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../redux/hooks';
import {
  createCsvTemplate,
  fetchCsvTemplateNameOptions,
  fetchCsvTemplate,
  updateCsvTemplate,
} from '../../../redux/modules/b2bThriftPlan/CsvTempletes/csvTempletes.actions';
import {
  csvTemplateNameOptionsSelector,
  csvTemplateNameOptionsLoadingSelector,
  createCsvTemplateLoadingSelector,
  updateCsvTemplateLoadingSelector,
  csvTemplateLoadingSelector,
  selectedCsvTemplateSelector,
} from '../../../redux/modules/b2bThriftPlan/CsvTempletes/csvTempletes.selectors';
import { locations } from '../../../routes/locations';

interface FormValues {
  templateName: string;
  file: File | null;
}

const fileValidation = yup
  .mixed<File>()
  .required('CSV file is required')
  .test('fileType', 'Only CSV files are allowed', (value) => {
    if (!value) return false;
    const fileName = value.name?.toLowerCase() || '';
    return value.type === 'text/csv' || fileName.endsWith('.csv');
  });

const CreateCsvTemplete = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { id } = useParams<{ id?: string }>();
  const templateId = id ? Number(id) : null;
  const isEdit = typeof templateId === 'number' && !Number.isNaN(templateId);

  const nameOptions = useSelector(csvTemplateNameOptionsSelector);
  const isNameOptionsLoading = useSelector(csvTemplateNameOptionsLoadingSelector);
  const isCreateLoading = useSelector(createCsvTemplateLoadingSelector);
  const isUpdateLoading = useSelector(updateCsvTemplateLoadingSelector);
  const isTemplateLoading = useSelector(csvTemplateLoadingSelector);
  const selectedTemplate = useSelector(selectedCsvTemplateSelector);
  const [hasRequestedTemplate, setHasRequestedTemplate] = useState(false);

  const editingTemplate = isEdit ? selectedTemplate || undefined : undefined;

  useEffect(() => {
    dispatch(fetchCsvTemplateNameOptions());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && templateId !== null) {
      dispatch(fetchCsvTemplate(templateId));
      setHasRequestedTemplate(true);
    }
  }, [dispatch, isEdit, templateId]);

  useEffect(() => {
    if (isEdit && hasRequestedTemplate && !isTemplateLoading && !editingTemplate) {
      enqueueSnackbar('CSV template not found.', { variant: 'warning' });
      navigate(locations.csvTemplates());
    }
  }, [enqueueSnackbar, navigate, isEdit, hasRequestedTemplate, isTemplateLoading, editingTemplate]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        templateName: isEdit ? yup.string() : yup.string().required('Template name is required'),
        file: fileValidation,
      }),
    [isEdit],
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      templateName: editingTemplate?.name || '',
      file: null,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (isEdit && templateId !== null) {
          if (!values.file) {
            formik.setFieldError('file', 'CSV file is required');
            setSubmitting(false);
            return;
          }

          const response = await dispatch(updateCsvTemplate({ id: templateId, file: values.file })).unwrap();
          const message = response?.data?.message || 'CSV template updated successfully!';
          enqueueSnackbar(message, { variant: 'success' });
        } else {
          if (!values.file) {
            formik.setFieldError('file', 'CSV file is required');
            setSubmitting(false);
            return;
          }

          await dispatch(createCsvTemplate({ name: values.templateName, file: values.file })).unwrap();
          enqueueSnackbar('CSV template uploaded successfully!', { variant: 'success' });
        }
        navigate(locations.csvTemplates());
      } catch (error: any) {
        const message = error?.message || 'Failed to submit CSV template';
        enqueueSnackbar(message, { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] || null;

    formik.setFieldTouched('file', true, false);

    if (!file) {
      formik.setFieldValue('file', null);
      return;
    }

    const lowerName = file.name?.toLowerCase() || '';
    const isCsv = file.type === 'text/csv' || lowerName.endsWith('.csv');

    if (!isCsv) {
      formik.setFieldError('file', 'Only CSV files are allowed');
      formik.setFieldValue('file', null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    formik.setFieldValue('file', file);
    formik.setFieldError('file', undefined);
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(
    () => () => {
      resetFileInput();
    },
    [],
  );

  const actionLoading = isEdit ? isUpdateLoading : isCreateLoading;
  const fileLabel = useMemo(() => {
    if (formik.values.file) {
      return formik.values.file.name;
    }
    if (isEdit && editingTemplate?.name) {
      return `Current file: ${editingTemplate.name}`;
    }
    return 'No file selected';
  }, [formik.values.file, isEdit, editingTemplate?.name]);

  return (
    <Container component="main" maxWidth="xl">
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        <Typography variant="h5">{isEdit ? 'Edit CSV Template' : 'Create CSV Template'}</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(formik.touched.templateName && formik.errors.templateName)}>
              <InputLabel id="template-name-label">Template Name</InputLabel>
              <Select
                labelId="template-name-label"
                id="templateName"
                name="templateName"
                label="Template Name"
                value={formik.values.templateName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isNameOptionsLoading || isEdit}
              >
                <MenuItem value="">
                  <em>Select template</em>
                </MenuItem>
                {isEdit && editingTemplate?.name && !nameOptions.includes(editingTemplate.name) && (
                  <MenuItem value={editingTemplate.name}>{editingTemplate.name}</MenuItem>
                )}
                {nameOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.templateName && formik.errors.templateName && (
                <FormHelperText>{formik.errors.templateName}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={Boolean(formik.touched.file && formik.errors.file)}>
              <input
                ref={fileInputRef}
                id="csv-file-input"
                name="file"
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <Button variant="outlined" onClick={() => fileInputRef.current?.click()} fullWidth>
                {formik.values.file ? 'Change CSV File' : 'Upload CSV File'}
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {fileLabel}
              </Typography>
              {isEdit && editingTemplate?.url && (
                <Button
                  component="a"
                  href={editingTemplate.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  sx={{ alignSelf: 'flex-start', mt: 1, px: 0 }}
                >
                  Download current file
                </Button>
              )}
              {formik.touched.file && formik.errors.file && <FormHelperText>{formik.errors.file}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={actionLoading || formik.isSubmitting || isTemplateLoading}
            disabled={
              !formik.isValid ||
              !formik.dirty ||
              isNameOptionsLoading ||
              (isEdit && isTemplateLoading) ||
              actionLoading ||
              formik.isSubmitting ||
              isTemplateLoading
            }
          >
            {isEdit ? 'Update' : 'Upload'}
          </LoadingButton>
          <Button variant="outlined" color="secondary" onClick={() => navigate(locations.csvTemplates())}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default CreateCsvTemplete;
